/**
 * Entity / status chip palettes — the ENTITY and STATUS maps from the design
 * (specs/tmext_mobility/design/axis-group-mobility.design.html), keyed off the
 * REAL backend identifiers. Pure lookup helpers (no React) so portals can style
 * entity chips, status pills and requisition badges consistently:
 *
 *   const { color, bg } = entityStyle(job.entity_name)
 *   <span style={{ color, background: bg }}>…</span>
 *
 * Unknown entities get a DETERMINISTIC hashed fallback from a small AA-safe
 * palette (same input → same colors, no flicker across renders/pages).
 */

export interface ChipStyle {
  /** Text color (dark, AA on `bg`). */
  color: string
  /** Chip background (light tint). */
  bg: string
}

// ── Entities ─────────────────────────────────────────────────────────────────────

/** Canonical per-entity palette, keyed by entity_code. */
export const ENTITY_COLORS: Record<string, ChipStyle> = {
  AXIS_BANK: { color: '#8E1537', bg: '#F7EAEF' },
  AXIS_FINANCE: { color: '#23407A', bg: '#E9EEF7' },
  AXIS_SECURITIES: { color: '#0E7C6B', bg: '#E4F2EF' },
}

/** Display-name aliases → entity_code (the API often exposes only entity_name). */
const ENTITY_NAME_TO_CODE: Record<string, string> = {
  'axis bank': 'AXIS_BANK',
  'axis finance': 'AXIS_FINANCE',
  'axis securities': 'AXIS_SECURITIES',
}

/** Deterministic fallback palette for entities outside the canonical three. */
const FALLBACK_ENTITY_PALETTE: ChipStyle[] = [
  { color: '#5B3A8E', bg: '#EFE9F7' }, // violet
  { color: '#8A4A0B', bg: '#F7EEE2' }, // ochre
  { color: '#0B5B7A', bg: '#E4EFF5' }, // steel blue
  { color: '#7A0B4E', bg: '#F7E4EE' }, // plum
  { color: '#3E6212', bg: '#ECF3E0' }, // olive
]

/** Tiny stable string hash (djb2) for the fallback palette pick. */
function hashString(input: string): number {
  let hash = 5381
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 33) ^ input.charCodeAt(i)
  }
  return Math.abs(hash)
}

/**
 * Resolve the chip palette for an entity, by `entity_code` (AXIS_BANK, …) OR by
 * display name ("Axis Bank", …). Unknown/empty values resolve deterministically
 * from the fallback palette — never a crash, never a random color.
 */
export function entityStyle(entityCodeOrName: string | null | undefined): ChipStyle {
  const raw = (entityCodeOrName ?? '').trim()
  if (!raw) return FALLBACK_ENTITY_PALETTE[0]

  const asCode = raw.toUpperCase().replace(/[\s-]+/g, '_')
  if (ENTITY_COLORS[asCode]) return ENTITY_COLORS[asCode]

  const viaName = ENTITY_NAME_TO_CODE[raw.toLowerCase()]
  if (viaName) return ENTITY_COLORS[viaName]

  return FALLBACK_ENTITY_PALETTE[hashString(asCode) % FALLBACK_ENTITY_PALETTE.length]
}

// ── Application statuses ─────────────────────────────────────────────────────────

/** The design's STATUS chip map, keyed by status_code (status-catalog codes). */
export const STATUS_COLORS: Record<string, ChipStyle> = {
  APPLIED: { color: '#475569', bg: '#EEF1F5' },
  UNDER_REVIEW: { color: '#92600A', bg: '#FBF0DA' },
  IN_PROGRESS: { color: '#1E5EAE', bg: '#E7EFFB' },
  CLOSED: { color: '#7B7680', bg: '#F1F1F3' },
  // Terminal/extended catalog codes, harmonized with the same palette:
  SELECTED: { color: '#0B6155', bg: '#E4F2EF' },
  ON_HOLD: { color: '#92600A', bg: '#FBF0DA' },
  WITHDRAWN: { color: '#7B7680', bg: '#F1F1F3' },
  REJECTED: { color: '#B42318', bg: '#FDECEA' },
  NOT_SELECTED: { color: '#B42318', bg: '#FDECEA' },
}

const NEUTRAL_STATUS: ChipStyle = { color: '#7B7680', bg: '#F1F1F3' }

/** Chip palette for an application status code; unknown codes → neutral gray. */
export function statusStyle(statusCode: string | null | undefined): ChipStyle {
  const code = (statusCode ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_')
  return STATUS_COLORS[code] ?? NEUTRAL_STATUS
}

// ── Eligibility basis ────────────────────────────────────────────────────────────

/**
 * The design's allow-list basis chips (HR allow-list + admin grade screen):
 * PLUS_ONE ("One band up") blue, SAME_GRADE ("Same grade") neutral gray.
 */
export const BASIS_COLORS: Record<string, ChipStyle> = {
  PLUS_ONE: { color: '#1E5EAE', bg: '#E7EFFB' },
  SAME_GRADE: { color: '#5B565F', bg: '#F1EEF2' },
}

/** Neutral fallback chip for unknown basis / generic gray chips. */
export const NEUTRAL_CHIP_STYLE: ChipStyle = { color: '#7B7680', bg: '#F1F1F3' }

// ── Requisition statuses ─────────────────────────────────────────────────────────

/**
 * Requisition badge palette per the design: Open (teal), Closing-soon (amber —
 * a DERIVED state, pass 'CLOSING_SOON' when visibility_end is near), Closed
 * (gray), Draft (muted lavender-gray).
 */
export const REQUISITION_STATUS_COLORS: Record<string, ChipStyle> = {
  OPEN: { color: '#0B6155', bg: '#E4F2EF' },
  CLOSING_SOON: { color: '#92600A', bg: '#FBF0DA' },
  CLOSED: { color: '#7B7680', bg: '#F1F1F3' },
  DRAFT: { color: '#5B565F', bg: '#F1EEF2' },
}

/** Chip palette for a requisition status; unknown values → the Draft neutrals. */
export function requisitionStatusStyle(status: string | null | undefined): ChipStyle {
  const code = (status ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_')
  return REQUISITION_STATUS_COLORS[code] ?? REQUISITION_STATUS_COLORS.DRAFT
}
